/**
 * Work List PDF — 세로 A4. `WorkListA4PreviewVertical`와 동일한 페이지·행·열 규칙.
 */

import React from 'react';
import { Document, Page, View, StyleSheet, Image } from '@react-pdf/renderer';
import { CvPdfText } from '../about/cvPdfShared';
import logoFit from '../../asset/gnb/logo_fit.png';
import {
  getWorkListPageStartIndex,
  getWorkListPdfTotalPageCount,
  getWorkListRowsOnPage,
} from './WorkListA4PreviewVertical';
import {
  getWorkListPdfColumnFlexes,
  getWorkListHeaderLabels,
  getWorkListOptionalColIndices,
  workListImageSource,
  type WorkListPdfTableRow,
} from './DocumentWorkListHorizontal';

const A4_PORTRAIT: [number, number] = [595, 842];

/** 1페이지만: 제목·부제 블록 아래에서 테이블이 시작되도록 상단 여백 */
const WORK_LIST_FIRST_PAGE_TABLE_TOP_PT = 108;
/** 2페이지 이후: 제목 없음 — 페이지 상단·로고와 겹치지 않을 최소 여백 */
const WORK_LIST_CONTINUATION_PAGE_TABLE_TOP_PT = 44;
const WORK_LIST_FOOTER_RESERVE_PT = 40;
/** 미리보기 행 높이 대비 PDF에서 약 3배 */
// const ROW_HEIGHT_PT = 34;
const ROW_HEIGHT_PT = 50;
/** `WorkListA4PreviewVertical`의 `column-gap: 0.8vh`에 맞춘 열 간격(pt) */
const WORK_LIST_TABLE_COLUMN_GAP_PT = 20;
/** 1페이지 테이블 추가 내림 — 세로 미리보기는 `2*ROW_H` 대신 `1.5*ROW_H` → 가로 대비 0.75배 */
const FIRST_PAGE_TABLE_EXTRA_PT = ROW_HEIGHT_PT * 0.375;

const asTrimmedString = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value).trim();
  return '';
};

const dashIfEmpty = (value: string): string => (value ? value : '-');

export interface DocumentWorkListVerticalProps {
  title: string;
  subtitle: string;
  gallery: string;
  baseItemsPerPage: number;
  includeArtist: boolean;
  includePrice: boolean;
  includeNote: boolean;
  tableRows: WorkListPdfTableRow[];
}

const DocumentWorkListVertical: React.FC<DocumentWorkListVerticalProps> = ({
  title,
  subtitle,
  gallery,
  baseItemsPerPage,
  includeArtist,
  includePrice,
  includeNote,
  tableRows,
}) => {
  const selectedCount = tableRows.length;
  const totalPages = getWorkListPdfTotalPageCount(selectedCount, baseItemsPerPage);
  const columnFlexes = getWorkListPdfColumnFlexes(includeArtist, includePrice, includeNote);

  const headerLabels = getWorkListHeaderLabels(
    { includeArtist, includePrice, includeNote },
  );
  const { price: priceColIndex, note: noteColIndex } =
    getWorkListOptionalColIndices(includeArtist, includePrice, includeNote);

  return (
    <Document>
      {Array.from({ length: totalPages }, (_, pageIdx) => {
        const pageNumber = pageIdx + 1;
        const startIndex = getWorkListPageStartIndex(baseItemsPerPage, pageNumber);
        const rowsOnPage = getWorkListRowsOnPage(baseItemsPerPage, pageNumber);
        const tableTopExtra = pageNumber === 1 ? FIRST_PAGE_TABLE_EXTRA_PT : 0;
        const tablePaddingTop =
          (pageNumber === 1 ? WORK_LIST_FIRST_PAGE_TABLE_TOP_PT : WORK_LIST_CONTINUATION_PAGE_TABLE_TOP_PT) +
          tableTopExtra;

        const slots: (WorkListPdfTableRow | null)[] = [];
        for (let r = 0; r < rowsOnPage; r += 1) {
          const globalIdx = startIndex + r;
          slots.push(globalIdx < tableRows.length ? tableRows[globalIdx] : null);
        }

        return (
          <Page key={`wl-pdf-portrait-${pageNumber}`} size={A4_PORTRAIT} style={PDFStyles.Page.root}>
            <View style={PDFStyles.Section.pageBody}>
              <View style={[PDFStyles.Section.tableOuter, { paddingTop: tablePaddingTop }]}>
                <View style={PDFStyles.Section.tableHeaderRow}>
                  {headerLabels.map((label, i) => (
                    <View key={`h-${label}`} style={{ flex: columnFlexes[i], minWidth: 0 }}>
                      <CvPdfText style={PDFStyles.Text.headerCell}>{label}</CvPdfText>
                    </View>
                  ))}
                </View>
                {slots.map((row, rowIndex) =>
                  row ? (
                    <View key={`d-${startIndex + rowIndex}`} style={PDFStyles.Section.dataRow}>
                      <View style={{ flex: columnFlexes[0], minWidth: 0, justifyContent: 'center' }}>
                        <CvPdfText style={PDFStyles.Text.indexCell}>{`${startIndex + rowIndex + 1}.`}</CvPdfText>
                      </View>
                      <View
                        style={{
                          flex: columnFlexes[1],
                          minWidth: 0,
                          alignItems: 'flex-start',
                          justifyContent: 'center',
                        }}
                      >
                        {asTrimmedString(row.thumbSrc) ? (
                          <Image source={workListImageSource(row.thumbSrc)} style={PDFStyles.Image.thumb} />
                        ) : (
                          <View style={PDFStyles.Section.thumbPlaceholder} />
                        )}
                      </View>
                      {[
                        ...(includeArtist ? [row.artist] : []),
                        row.title,
                        row.year,
                        row.material,
                        row.sizeLine,
                      ].map((value, index) => (
                        <View
                          key={`core-${index}`}
                          style={{ flex: columnFlexes[index + 2], minWidth: 0, justifyContent: 'center' }}
                        >
                          <CvPdfText style={PDFStyles.Text.cell}>{value}</CvPdfText>
                        </View>
                      ))}
                      {includePrice && (
                        <View style={{ flex: columnFlexes[priceColIndex], minWidth: 0, justifyContent: 'center' }}>
                          <CvPdfText style={PDFStyles.Text.cell}>{row.priceLine}</CvPdfText>
                        </View>
                      )}
                      {includeNote && (
                        <View style={{ flex: columnFlexes[noteColIndex], minWidth: 0, justifyContent: 'center' }}>
                          <CvPdfText style={PDFStyles.Text.cell}>{row.noteLine}</CvPdfText>
                        </View>
                      )}
                    </View>
                  ) : (
                    <View key={`e-${startIndex + rowIndex}`} style={PDFStyles.Section.emptyRow}>
                      {columnFlexes.map((flex, ci) => (
                        <View
                          key={`ec-${startIndex + rowIndex}-${ci}`}
                          style={[PDFStyles.Section.emptyRowCell, { flex, minWidth: 0 }]}
                        />
                      ))}
                    </View>
                  )
                )}
              </View>
            </View>

            {pageNumber === 1 ? (
              <View style={PDFStyles.Section.page1HeaderLeft}>
                <CvPdfText style={PDFStyles.Text.titleLine}>{dashIfEmpty(title)}</CvPdfText>
                <CvPdfText style={PDFStyles.Text.metaLine}>{subtitle}</CvPdfText>
              </View>
            ) : null}

            <View style={PDFStyles.Section.fixedLogoWrap} fixed>
              <Image source={logoFit} style={PDFStyles.Image.logo} />
            </View>

            <View style={PDFStyles.Section.fixedFooterRow} fixed>
              <View style={PDFStyles.Section.footerCell}>
                <CvPdfText style={PDFStyles.Text.footerLeft}>{gallery}</CvPdfText>
              </View>
              <View style={PDFStyles.Section.footerCellRight}>
                <CvPdfText style={PDFStyles.Text.footerRight}>
                  {`${pageNumber} / ${totalPages}`}
                </CvPdfText>
              </View>
            </View>
          </Page>
        );
      })}
    </Document>
  );
};

const PDFTypography = {
  titleLine: { fontSize: 14, fontFamily: 'NotoKR', fontWeight: 600 },
  metaLine: { fontSize: 10, fontFamily: 'NotoKR', fontWeight: 400 },
  headerCell: { fontSize: 11, fontFamily: 'NotoKR', fontWeight: 600 },
  indexCell: { fontSize: 9, fontFamily: 'NotoKR', fontWeight: 500 },
  cell: { fontSize: 9, fontFamily: 'NotoKR', fontWeight: 400 },
  footer: { fontSize: 7, fontFamily: 'NotoKR', fontWeight: 400 },
};

const PDFStyles = {
  Page: StyleSheet.create({
    root: {
      paddingTop: 24,
      paddingBottom: 20,
      paddingLeft: 22,
      paddingRight: 22,
      backgroundColor: '#ffffff',
      fontFamily: 'NotoKR',
      fontWeight: 400,
    },
  }),

  Image: StyleSheet.create({
    logo: {
      width: 16,
      height: 16,
      objectFit: 'contain' as const,
    },
    thumb: {
      maxWidth: '100%',
      maxHeight: ROW_HEIGHT_PT - 4,
      objectFit: 'contain' as const,
      objectPosition: 'left center' as const,
    },
  }),

  Section: StyleSheet.create({
    pageBody: {
      flex: 1,
      width: '100%',
      minHeight: 0,
      flexDirection: 'column',
      paddingBottom: WORK_LIST_FOOTER_RESERVE_PT,
    },
    tableOuter: {
      width: '100%',
      flexDirection: 'column',
    },
    tableHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: WORK_LIST_TABLE_COLUMN_GAP_PT,
      width: '100%',
      minHeight: ROW_HEIGHT_PT,
      borderBottomWidth: 0.75,
      borderBottomColor: '#000000',
      borderBottomStyle: 'solid',
      paddingBottom: 4,
    },
    dataRow: {
      flexDirection: 'row',
      alignItems: 'center',
      columnGap: WORK_LIST_TABLE_COLUMN_GAP_PT,
      width: '100%',
      minHeight: ROW_HEIGHT_PT,
      borderBottomWidth: 0.5,
      borderBottomColor: '#c7c7c7',
      borderBottomStyle: 'solid',
      paddingTop: 4,
      paddingBottom: 4,
    },
    emptyRow: {
      flexDirection: 'row',
      alignItems: 'stretch',
      columnGap: WORK_LIST_TABLE_COLUMN_GAP_PT,
      width: '100%',
      minHeight: ROW_HEIGHT_PT,
      borderBottomWidth: 0.5,
      borderBottomColor: '#cccccc',
      borderBottomStyle: 'solid',
    },
    emptyRowCell: {
      minHeight: ROW_HEIGHT_PT - 8,
      borderBottomWidth: 0,
    },
    thumbPlaceholder: {
      width: 32,
      height: 32,
    },
    page1HeaderLeft: {
      position: 'absolute',
      top: 24,
      left: 22,
      maxWidth: '65%',
      flexDirection: 'column',
    },
    fixedLogoWrap: {
      position: 'absolute',
      top: 24,
      right: 22,
    },
    fixedFooterRow: {
      position: 'absolute',
      bottom: 20,
      left: 22,
      right: 22,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
    },
    footerCell: {
      flex: 1,
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    footerCellRight: {
      flexShrink: 0,
      flexDirection: 'column',
      alignItems: 'flex-end',
      justifyContent: 'flex-end',
      minWidth: 72,
    },
  }),

  Text: StyleSheet.create({
    titleLine: { ...PDFTypography.titleLine, marginBottom: 1 },
    metaLine: { ...PDFTypography.metaLine },
    headerCell: { ...PDFTypography.headerCell },
    indexCell: { ...PDFTypography.indexCell },
    cell: { ...PDFTypography.cell },
    footerLeft: { ...PDFTypography.footer, textAlign: 'left' as const },
    footerRight: { ...PDFTypography.footer, textAlign: 'right' as const },
  }),
};

export default DocumentWorkListVertical;
